import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import { Check, Circle, X, Sparkles } from "lucide-react";
import { useActivationStatus, type ActivationStepId } from "@/hooks/use-activation-status";

const STEP_LABEL_KEYS: Record<ActivationStepId, string> = {
  profile:       "gettingStarted.stepProfile",
  firstLog:      "gettingStarted.stepFirstLog",
  plateTalk:     "gettingStarted.stepPlateTalk",
  calorieTarget: "gettingStarted.stepCalorieTarget",
  hydration:     "gettingStarted.stepHydration",
};

export function GettingStartedChecklist() {
  const { t } = useTranslation();
  const { steps, completedCount, totalCount, visible, dismiss } = useActivationStatus();

  if (!visible) return null;
  const pct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <div
      data-testid="getting-started-checklist"
      className="mx-3 my-3 rounded-lg border border-primary/25 bg-primary/5 p-3 shadow-[0_0_18px_-6px_hsl(var(--primary)/0.4)]"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-primary" />
          <span className="text-[11px] font-bold uppercase tracking-wider text-primary">
            {t("gettingStarted.title")}
          </span>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="text-muted-foreground hover:text-foreground transition-colors"
          aria-label={t("gettingStarted.dismiss")}
          data-testid="getting-started-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="flex items-center gap-2 mb-2.5">
        <div className="flex-1 h-1.5 rounded-full bg-muted/40 overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="text-[10px] font-mono text-muted-foreground shrink-0">
          {completedCount}/{totalCount}
        </span>
      </div>
      <ul className="space-y-1">
        {steps.map(step => (
          <li key={step.id}>
            <Link
              href={step.href}
              className={`flex items-center gap-2 text-[11px] py-1 px-1 rounded transition-colors ${
                step.done
                  ? "text-muted-foreground/70 line-through"
                  : "text-foreground hover:text-primary"
              }`}
              data-testid={`getting-started-step-${step.id}`}
            >
              {step.done ? (
                <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
              ) : (
                <Circle className="w-3.5 h-3.5 text-muted-foreground/50 shrink-0" />
              )}
              <span className="truncate">{t(STEP_LABEL_KEYS[step.id])}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
